import type { Prompt, PromptSetWithCount } from '@meridian-ssa/shared';
import { useEffect, useState } from 'react';

import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

import { Button } from '../ui/Button';
import { InputField, TextareaField } from '../ui/FormField';
import { Modal } from '../ui/Modal';

interface PromptSetManagerProps {
  onClose: () => void;
  onSelectSet?: (setId: string, promptIds: string[]) => void;
}

export function PromptSetManager({
  onClose,
  onSelectSet,
}: PromptSetManagerProps) {
  const [sets, setSets] = useState<PromptSetWithCount[]>([]);
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [selectedSetPrompts, setSelectedSetPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchSets();
  }, []);

  const fetchSets = async () => {
    try {
      setLoading(true);
      const data = await apiRequest('/api/eval/prompt-sets');
      setSets(data.promptSets || []);
    } catch (error) {
      logError('Error fetching prompt sets', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSet = async (setId: string) => {
    try {
      setLoading(true);
      const data = await apiRequest(`/api/eval/prompt-sets/${setId}`);
      setSelectedSetId(setId);
      setSelectedSetPrompts(data.prompts || []);
    } catch (error) {
      logError('Error fetching set details', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUseSet = () => {
    if (selectedSetId && onSelectSet) {
      const promptIds = selectedSetPrompts.map(p => p.id);
      onSelectSet(selectedSetId, promptIds);
      onClose();
    }
  };

  const handleDeleteSet = async (setId: string) => {
    if (!confirm('Are you sure you want to delete this prompt set?')) return;

    try {
      await apiRequest(`/api/eval/prompt-sets/${setId}`, {
        method: 'DELETE',
      });

      alert('Prompt set deleted successfully');
      fetchSets();
      if (selectedSetId === setId) {
        setSelectedSetId(null);
        setSelectedSetPrompts([]);
      }
    } catch (error: any) {
      alert(error.message);
    }
  };

  return (
    <Modal title="Manage Prompt Sets" onClose={onClose} maxWidth="800px">
      <div style={{ display: 'flex', gap: '1rem', minHeight: '400px' }}>
        {/* Left panel: List of sets */}
        <div
          style={{
            flex: 1,
            borderRight: '1px solid #ddd',
            paddingRight: '1rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
            }}
          >
            <h3 style={{ margin: 0 }}>Sets</h3>
            <Button size="small" onClick={() => setShowCreateModal(true)}>
              + New Set
            </Button>
          </div>

          {loading && !sets.length ? (
            <div style={{ color: '#666' }}>Loading...</div>
          ) : sets.length === 0 ? (
            <div style={{ color: '#666' }}>No prompt sets yet</div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}
            >
              {sets.map(set => (
                <div
                  key={set.id}
                  onClick={() => handleSelectSet(set.id)}
                  style={{
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border:
                      selectedSetId === set.id
                        ? '2px solid #6366f1'
                        : '1px solid #ddd',
                    cursor: 'pointer',
                    background: selectedSetId === set.id ? '#f0f0ff' : 'white',
                  }}
                >
                  <div style={{ fontWeight: 500 }}>{set.name}</div>
                  <div style={{ fontSize: '0.875rem', color: '#666' }}>
                    {set.prompt_count} prompt{set.prompt_count !== 1 ? 's' : ''}
                  </div>
                  {set.description && (
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: '#888',
                        marginTop: '0.25rem',
                      }}
                    >
                      {set.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right panel: Set details */}
        <div style={{ flex: 1 }}>
          {selectedSetId ? (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem',
                }}
              >
                <h3 style={{ margin: 0 }}>Prompts</h3>
                <Button
                  size="small"
                  variant="danger"
                  onClick={() => handleDeleteSet(selectedSetId)}
                >
                  Delete Set
                </Button>
              </div>

              {selectedSetPrompts.length === 0 ? (
                <div style={{ color: '#666' }}>This set has no prompts</div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    maxHeight: '300px',
                    overflow: 'auto',
                  }}
                >
                  {selectedSetPrompts.map(prompt => (
                    <div
                      key={prompt.id}
                      style={{
                        padding: '0.75rem',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        background: 'white',
                      }}
                    >
                      <div style={{ fontWeight: 500 }}>{prompt.name}</div>
                      {(prompt.category || prompt.difficulty) && (
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: '#666',
                            marginTop: '0.25rem',
                          }}
                        >
                          {prompt.category && <span>{prompt.category}</span>}
                          {prompt.category && prompt.difficulty && (
                            <span> • </span>
                          )}
                          {prompt.difficulty && (
                            <span>{prompt.difficulty}</span>
                          )}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: '0.875rem',
                          color: '#888',
                          marginTop: '0.5rem',
                        }}
                      >
                        {prompt.text.substring(0, 100)}...
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {onSelectSet && selectedSetPrompts.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <Button fullWidth onClick={handleUseSet}>
                    Use This Set ({selectedSetPrompts.length} prompts)
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div
              style={{ color: '#666', textAlign: 'center', marginTop: '2rem' }}
            >
              Select a set to view details
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateSetModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchSets();
          }}
        />
      )}
    </Modal>
  );
}

interface CreateSetModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateSetModal = ({ onClose, onSuccess }: CreateSetModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      await apiRequest('/api/eval/prompt-sets', {
        method: 'POST',
        body: JSON.stringify({ name, description }),
      });

      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Create New Prompt Set" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {error && (
          <div
            style={{
              padding: '0.75rem',
              background: '#fee',
              borderRadius: '4px',
              color: '#c00',
            }}
          >
            {error}
          </div>
        )}

        <InputField
          label="Set Name"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g., Q1 2025 Test Cases"
          required
        />

        <TextareaField
          label="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Optional description"
        />

        <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}
        >
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading || !name}
            loading={loading}
          >
            Create Set
          </Button>
        </div>
      </div>
    </Modal>
  );
};
